'use client';

import { useCallback } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { StoredHarDisplayEntry } from '@terra/shared-utils';
import { MetaDefault } from '../../components/typography';
import { HttpEditor } from '../http-editor';
import type { HttpEditorProps } from '../http-editor/types';
import { useChatHarOptional } from '../chat/har/ChatHarProvider';
import { useSendToReplay } from '../chat/http-replay';
import { useSendHttpToChat } from '../chat/http-send-to-chat';
import { useHarEntryHttpDisplay } from './useHarEntryHttpDisplay';
import { buildHarDisplayEditorProps } from './build-har-display-editor-props';

type HarEntryRequestResponseEditorsProps = {
  harEntry: StoredHarDisplayEntry;
  viewResetKey?: string;
  height?: string;
  requestTitle?: string;
  responseTitle?: string;
  responseStatusCode?: number;
  canonicalRequestUrl?: string;
  requestEditorProps?: Omit<
    HttpEditorProps,
    | 'value'
    | 'readOnly'
    | 'showViewModeToggle'
    | 'toolbarContent'
    | 'showGrpcBodyViewToggle'
    | 'grpcBodyView'
    | 'onGrpcBodyViewChange'
  >;
  responseEditorProps?: Omit<
    HttpEditorProps,
    | 'value'
    | 'readOnly'
    | 'showViewModeToggle'
    | 'toolbarContent'
    | 'showGrpcBodyViewToggle'
    | 'grpcBodyView'
    | 'onGrpcBodyViewChange'
  >;
};

export function HarEntryRequestResponseEditors({
  harEntry,
  viewResetKey,
  height = '100%',
  requestTitle,
  responseTitle,
  responseStatusCode,
  canonicalRequestUrl,
  requestEditorProps,
  responseEditorProps,
}: HarEntryRequestResponseEditorsProps) {
  const chatHar = useChatHarOptional();
  const sendRawToReplay = useSendToReplay();
  const sendRawToChat = useSendHttpToChat();
  const {
    bodyView,
    setBodyView,
    hasDecodedBodyView,
    requestContent,
    responseContent,
  } = useHarEntryHttpDisplay({ harEntry, viewResetKey });
  const sendHarEntryToReplay = useCallback(
    (content: string) => {
      sendRawToReplay?.(content, { harEntry });
    },
    [sendRawToReplay, harEntry],
  );
  const sendRequestToChat = useCallback(
    (content: string, selectedText?: string) => {
      sendRawToChat?.({
        content,
        selectedText,
        responseContent,
      });
    },
    [sendRawToChat, responseContent],
  );
  const sendResponseToChat = useCallback(
    (content: string, selectedText?: string) => {
      sendRawToChat?.({
        content,
        selectedText,
        requestContent,
      });
    },
    [sendRawToChat, requestContent],
  );
  const requestSendToReplay =
    requestEditorProps?.onSendToReplay ??
    (sendRawToReplay ? sendHarEntryToReplay : undefined);
  const requestSendToChat =
    requestEditorProps?.onSendToChat ??
    (sendRawToChat ? sendRequestToChat : undefined);
  const responseSendToChat =
    responseEditorProps?.onSendToChat ??
    (sendRawToChat ? sendResponseToChat : undefined);
  const showProtocolBodyViewToggle =
    hasDecodedBodyView && (chatHar?.protocolDecodeEnabled ?? true);
  const showColumnHeaders = Boolean(requestTitle || responseTitle);
  const responseToolbarSpacer = requestSendToReplay ? (
    <Box w="5" h="5" flexShrink={0} aria-hidden visibility="hidden" />
  ) : null;
  const editorProps = buildHarDisplayEditorProps({
    showProtocolBodyViewToggle,
    bodyView,
    onBodyViewChange: setBodyView,
  });

  return (
    <VStack alignItems="stretch" gap="0" width="100%" h={height}>
      {showColumnHeaders ? (
        <HStack
          alignItems="stretch"
          w="full"
          bg="bg.subtle"
          borderBottomWidth="1px"
          borderColor="border.muted"
          flexShrink={0}
        >
          <Box
            flex="1"
            px="2"
            py="1"
            minH="8"
            display="flex"
            alignItems="center"
          >
            {requestTitle ? (
              <MetaDefault fontWeight="medium">{requestTitle}</MetaDefault>
            ) : null}
          </Box>
          <Box w="1px" bg="border.subtle" alignSelf="stretch" />
          <Box
            flex="1"
            px="2"
            py="1"
            minH="8"
            display="flex"
            alignItems="center"
          >
            {responseTitle ? (
              <HStack gap="2">
                <MetaDefault fontWeight="medium">{responseTitle}</MetaDefault>
                {responseStatusCode !== undefined ? (
                  <MetaDefault color="fg.muted">
                    {responseStatusCode}
                  </MetaDefault>
                ) : null}
              </HStack>
            ) : null}
          </Box>
        </HStack>
      ) : null}
      <HStack alignItems="stretch" gap="0" width="100%" flex="1" minH={0}>
        <Box flex="1" minH={0} minW={0} h="full">
          <HttpEditor
            value={requestContent}
            readOnly
            showViewModeToggle
            canonicalRequestUrl={canonicalRequestUrl}
            {...editorProps}
            {...requestEditorProps}
            onSendToReplay={requestSendToReplay}
            onSendToChat={requestSendToChat}
          />
        </Box>

        <Box w="1px" bg="border.subtle" flexShrink={0} />

        <Box flex="1" minH={0} minW={0} h="full">
          <HttpEditor
            value={responseContent}
            readOnly
            showViewModeToggle
            toolbarContent={responseToolbarSpacer}
            {...editorProps}
            {...responseEditorProps}
            onSendToChat={responseSendToChat}
          />
        </Box>
      </HStack>
    </VStack>
  );
}
